import produce from 'immer';
import { useContext, useEffect } from 'react';
import { useRecoilCallback, useRecoilState } from 'recoil';

import { GraphQLOperationFailed } from '../../../../adapter/GraphQLClient';
import ProjectEditingStep from '../../../../entity/ProjectEditingStep';
import {
  DynamicSchemaDefinition,
  ProjectDetailsFragment,
  ProjectDetailsWithoutPricingDataFragment,
  useCreateProjectAsDraftMutation,
  useGetProjectTypeLazyQuery,
  useSubmitProjectMutation,
  useUpdateDraftProjectFromOptionScreenToPreviewScreenMutation,
  useUpdateDraftProjectMutation,
  useUpdateDraftProjectOnOptionScreenMutation,
  useUpdateDraftProjectToOptionScreenMutation,
  useUpdateProjectAssetCategoryValueMutation,
  useUpdateProjectMutation,
} from '../../../../generated/graphql';
import { context as DependencyContext } from '../../../context/dependency';
import { context as HistoryContext } from '../../../context/history';
import { context as StateContext } from '../../../context/state';
import { DynamicSchemaValue } from '../../../types/DynamicSchema';
import { EditingProjectState } from '../../../types/EditingProjectState';
import { ExtendedProject } from '../../../types/ExtendedProject';
import { editingProjectStateAtom } from '../atoms/editingProjectStateAtom';
import { projectAtom } from '../atoms/projectAtom';
import { ProjectStateNotPreparedError } from '../domain/errors';
import { convertProjectDynamicSchemaValues } from '../domain/projectPropertyConverter';
import { projectPropertyRecaster, stateToUpdateProjectVars } from '../domain/projectPropertyRecaster';

// Check if projectType is type ProjectDetailsFragment['projectType']
// Use the `is` type predicate instead of checking for a truthy field value that only exists in one object.
// (as pointed out in https://github.com/kaizenplatform/hugot-director-webclient/pull/2296#discussion_r877770203)
export const isProjectInstanceOfProjectDetailsFragment = (
  projectType: ProjectDetailsFragment['projectType'] | ProjectDetailsWithoutPricingDataFragment['projectType'],
): projectType is ProjectDetailsFragment['projectType'] => Object.hasOwnProperty.call(projectType, 'key');

const useUpdateProject = () => {
  const {
    pricingMerchandiseOptions: { selectedPricingMerchandiseOptions },
    projectEditing: { discountCode },
    language,
  } = useContext(StateContext);
  const history = useContext(HistoryContext);
  const {
    editProjectUsecase: {
      showFatalErrorMessage,
      showNonFatalErrorMessage,
      notifyUnimportantMessage,
      updateWindowDataLayer,
      setFullScreenLoader,
    },
  } = useContext(DependencyContext);

  const [project, setProject] = useRecoilState(projectAtom);

  const [callCreateDraftProject] = useCreateProjectAsDraftMutation({
    fetchPolicy: 'no-cache',
  });
  const [callUpdateProjectAssetCategoryValue] = useUpdateProjectAssetCategoryValueMutation({
    fetchPolicy: 'no-cache',
  });

  // Projectのアップデート周りのmutation
  const [callUpdateDraftProject, { data: updatedDraftProjectData }] = useUpdateDraftProjectMutation({
    fetchPolicy: 'no-cache',
  });
  const [
    callUpdateDraftProjectToOptionScreen,
    { data: updatedDraftProjectToOptionScreenData },
  ] = useUpdateDraftProjectToOptionScreenMutation({
    fetchPolicy: 'no-cache',
  });
  const [
    callUpdateDraftProjectOnOptionScreen,
    { data: updatedDraftProjectOnOptionScreenData },
  ] = useUpdateDraftProjectOnOptionScreenMutation({
    fetchPolicy: 'no-cache',
  });
  const [
    callUpdateDraftProjectFromOptionScreenToPreviewScreen,
    { data: updatedDraftProjectFromOptionScreenToPreviewScreenData },
  ] = useUpdateDraftProjectFromOptionScreenToPreviewScreenMutation({
    fetchPolicy: 'no-cache',
  });
  const [callUpdateSubmittedProject, { data: updatedSubmittedProjectData }] = useUpdateProjectMutation({
    fetchPolicy: 'no-cache',
  });
  const [callSubmitProject, { data: submittedProjectData }] = useSubmitProjectMutation({
    fetchPolicy: 'no-cache',
  });

  const [getProjectType] = useGetProjectTypeLazyQuery({ fetchPolicy: 'no-cache' });

  const createDraftProject = async ({
    organizationId,
    projectTypeKey,
  }: {
    organizationId: string;
    projectTypeKey: string;
  }) => {
    setFullScreenLoader(true);
    const { data: projectTypeData } = await getProjectType({
      variables: {
        key: projectTypeKey,
        language,
      },
    });

    if (projectTypeData?.projectType?.assetCategoryDefinition == null) {
      throw new Error('projectType.assetCategoryDefinition must not be null.');
    }

    const creativeBriefValue = convertProjectDynamicSchemaValues({
      definition: projectTypeData.projectType.creativeBriefDefinition,
      values: {},
    });

    const assetCategoryValue = convertProjectDynamicSchemaValues({
      definition: projectTypeData.projectType.assetCategoryDefinition as DynamicSchemaDefinition,
      values: {},
    });

    const { data: createDraftProjectData } = await callCreateDraftProject({
      variables: {
        title: 'Untitled Project',
        matchingFormat: 'auto',
        projectTypeId: projectTypeData.projectType.id,
        creationSet: null,
        creativeBriefDefinitionId: projectTypeData.projectType.creativeBriefDefinition.id,
        creativeBriefValues: { fields: creativeBriefValue?.values },
        assetCategoryDefinitionId: projectTypeData.projectType.assetCategoryDefinition.id,
        assetCategoryValues: { fields: assetCategoryValue?.values },
        organizationId,
      },
    });

    setFullScreenLoader(false);

    if (createDraftProjectData?.createProjectAsDraft == null)
      throw new Error('Failed to retrieve newly created draft project');

    history.replace(`/projects/${createDraftProjectData.createProjectAsDraft.id}/edit`);
  };

  const updateDraftProject = useRecoilCallback(({ snapshot }) => async () => {
    const state = await snapshot.getPromise(editingProjectStateAtom);

    if (project == null) throw new ProjectStateNotPreparedError();

    setFullScreenLoader(true);

    const variables = stateToUpdateProjectVars({
      state,
      projectId: project.id,
      options: selectedPricingMerchandiseOptions,
    });

    try {
      await callUpdateDraftProject({ variables });
      setFullScreenLoader(false);
    } catch (error) {
      setFullScreenLoader(false);
      if (error instanceof GraphQLOperationFailed && error.type === 'ValidationError') {
        throw new Error('message.project_update_as_draft_has_failed');
      }
      showNonFatalErrorMessage(error);
      return;
    }
  });

  const updateDraftProjectAndGoToDifferentTab = async ({
    state,
    isNotificationStop,
    currentStep,
    targetStep,
  }: {
    state: EditingProjectState;
    isNotificationStop?: boolean;
    currentStep: ProjectEditingStep;
    targetStep: ProjectEditingStep;
  }) => {
    if (project == null) throw new ProjectStateNotPreparedError();

    setFullScreenLoader(true);

    const variables = stateToUpdateProjectVars({
      state,
      projectId: project.id,
      options: selectedPricingMerchandiseOptions,
    });

    try {
      // isFlatの既存実装[1]の関係でMutationを最適化出来るものと出来ないものがある。
      // [1] https://github.com/kaizenplatform/hugot-director-webclient/pull/2242
      //
      // Mutationに入れるpropertyと返り値が両方最適化出来るケース
      // e.g. option(最適化出来る) => preview(最適化出来る), preview(最適化出来る) => option(最適化出来る)
      if (currentStep === 'option' && targetStep === 'preview') {
        await callUpdateDraftProjectFromOptionScreenToPreviewScreen({
          variables: {
            ...state,
            id: project.id,
            creationSet: projectPropertyRecaster(state).creationSet(),
            selectedPricingMerchandiseOptionIds: selectedPricingMerchandiseOptions,
          },
        });
      }

      // Mutationに入れるpropertyのみ、もしくはMutationの返り値のみを最適化出来るケース
      // e.g. option(最適化出来る) => brief(最適化出来ない), format(最適化出来ない) => preview(最適化出来る)
      else if (targetStep === 'option') {
        await callUpdateDraftProjectToOptionScreen({ variables });
      } else if (currentStep === 'option') {
        await callUpdateDraftProjectOnOptionScreen({
          variables: {
            ...state,
            id: project.id,
            creationSet: projectPropertyRecaster(state).creationSet(),
            selectedPricingMerchandiseOptionIds: selectedPricingMerchandiseOptions,
          },
        });
      } else {
        await callUpdateDraftProject({ variables });
      }

      setFullScreenLoader(false);

      if (!isNotificationStop) notifyUnimportantMessage('message.project_has_been_saved_as_draft');
    } catch (error) {
      setFullScreenLoader(false);
      if (error instanceof GraphQLOperationFailed && error.type === 'ValidationError') {
        throw new Error('message.project_update_as_draft_has_failed');
      }
      showNonFatalErrorMessage(error);
      return;
    }

    history.push(`/projects/${project.id}/edit?step=${targetStep}&org=${project.organization.id}`);
  };

  const updateDraftProjectAndGoToProjectsList = async ({
    state,
    currentStep,
    isNotificationStop,
  }: {
    state: EditingProjectState;
    currentStep: ProjectEditingStep;
    isNotificationStop?: boolean;
  }) => {
    if (project == null) {
      throw new Error('State `project` has not been prepared.');
    }

    setFullScreenLoader(true);

    const variables = stateToUpdateProjectVars({
      state,
      projectId: project.id,
      options: selectedPricingMerchandiseOptions,
    });

    try {
      if (currentStep === 'option') {
        await callUpdateDraftProjectOnOptionScreen({
          variables: {
            ...state,
            id: project.id,
            creationSet: projectPropertyRecaster(state).creationSet(),
            selectedPricingMerchandiseOptionIds: selectedPricingMerchandiseOptions,
          },
        });
      } else {
        await callUpdateDraftProject({ variables });
      }

      setFullScreenLoader(false);

      if (!isNotificationStop) notifyUnimportantMessage('message.project_has_been_saved_as_draft');
    } catch (error) {
      setFullScreenLoader(false);

      if (error instanceof GraphQLOperationFailed && error.type === 'ValidationError') {
        throw new Error('message.project_update_as_draft_has_failed');
      }
      showNonFatalErrorMessage(error);
      return;
    }

    history.push(`/projects?org=${project.organization.id}`);
  };

  const submitProject = async ({ state, pointTotal = 0 }: { state: EditingProjectState; pointTotal?: number }) => {
    if (project == null) throw new ProjectStateNotPreparedError();

    setFullScreenLoader(true);

    try {
      const variables = stateToUpdateProjectVars({
        state,
        projectId: project.id,
        options: selectedPricingMerchandiseOptions,
      });

      const { data: updatedProjectData } = await callUpdateDraftProject({
        variables,
      });

      if (updatedProjectData?.updateProjectAsDraft == null)
        throw new Error('message.project_update_as_draft_has_failed');

      // TODO: submittedのデータは使わないので、graphqlのqueryの引っ張ってくるfieldを劇的に減らせる
      const { data: submittedProjectData } = await callSubmitProject({
        variables: {
          id: updatedProjectData.updateProjectAsDraft.id,
          code: discountCode?.discountCode || undefined,
          agreedWithCreativeVisibility: project.agreedWithCreativeVisibility || true,
        },
      });

      if (submittedProjectData?.submitProject == null) throw new Error('Failed to submit project');

      updateWindowDataLayer((submittedProjectData.submitProject as unknown) as ExtendedProject, pointTotal);

      setFullScreenLoader(false);

      notifyUnimportantMessage('message.project_has_been_published');

      history.push(`/projects/${project.id}/edit?step=complete`);
    } catch (error) {
      setFullScreenLoader(false);
      if (error.type === 'PointShortage') {
        showFatalErrorMessage(new Error('message.point_shortage'));
      } else {
        showNonFatalErrorMessage(error);
      }
      return;
    }
  };

  const updateSubmittedProject = async ({ state }: { state: EditingProjectState }) => {
    if (project == null) throw new ProjectStateNotPreparedError();

    setFullScreenLoader(true);

    try {
      const recaster = projectPropertyRecaster(state);
      const { data: updatedProjectData } = await callUpdateSubmittedProject({
        variables: {
          id: project.id,
          title: recaster.title(),
          creativeBriefValues: { fields: state.creativeBriefValue?.values },
          assetCategoryValues: { fields: state.assetCategoryValue?.values },
          versionId: project.versionId || '',
        },
      });

      if (updatedProjectData?.updateProject?.project) {
        window.localStorage.removeItem(`project_brief_${project.id}`);
        notifyUnimportantMessage('message.project_has_been_submitted_as_edit');
        history.push(`/projects/${project.id}/brief`);

        if (!updatedProjectData?.updateProject.updated) {
          notifyUnimportantMessage('message.project_not_updated');
        }
      }
      setFullScreenLoader(false);
    } catch (error) {
      setFullScreenLoader(false);
      if (error instanceof GraphQLOperationFailed && error.type === 'ProjectVersionNotLatestError') {
        showNonFatalErrorMessage(new Error('message.project_update_has_failed'));
        history.push(`/projects/${project.id}/brief`);
      }
    }
  };

  const setCreativeVisibility = (checked: boolean): void => {
    setProject(
      produce(project, (draft) => {
        if (draft == null) return;
        draft.agreedWithCreativeVisibility = checked;
      }),
    );
  };

  const saveDraftEditOfSubmittedProject = ({ state }: { state: EditingProjectState }) => {
    if (project == null) throw new ProjectStateNotPreparedError();

    setFullScreenLoader(true);

    const projectBrief: ExtendedProject = {
      ...project,
      title: state.title || project?.title,
      creativeBriefValues: state.creativeBriefValue?.values || project?.creativeBriefValues,
      assetCategoryValues: state.assetCategoryValue?.values || project?.assetCategoryValues,
    };

    window.localStorage.setItem(`project_brief_${project.id}`, JSON.stringify(projectBrief));

    setFullScreenLoader(false);

    notifyUnimportantMessage('message.project_has_been_saved_as_draft');
    history.push(`/projects/${project.id}/brief`);
  };

  // DraftについてAssetCategoryValueを自動的にサーバに保存するときに使うメソッド
  // Draft以外の場合は下書き保存でAssetCategoryValueを更新してはいけないのでAPIを呼び出さずreturnする
  const updateAssetCategoryValueInDraft = (assetCategoryValue: DynamicSchemaValue | null) => {
    if (project == null) throw new ProjectStateNotPreparedError();
    if (project.state !== 'draft') return;

    callUpdateProjectAssetCategoryValue({
      variables: {
        id: project.id,
        assetCategoryValues: { fields: assetCategoryValue?.values },
      },
    });
  };

  useEffect(() => {
    if (updatedDraftProjectData?.updateProjectAsDraft) {
      setProject(updatedDraftProjectData.updateProjectAsDraft as ExtendedProject);
    }
  }, [setProject, updatedDraftProjectData?.updateProjectAsDraft]);

  useEffect(() => {
    if (submittedProjectData?.submitProject) {
      setProject(submittedProjectData?.submitProject as ExtendedProject);
    }
  }, [setProject, submittedProjectData?.submitProject]);

  useEffect(() => {
    if (updatedSubmittedProjectData?.updateProject?.project) {
      setProject(updatedSubmittedProjectData?.updateProject?.project as ExtendedProject);
    }
  }, [setProject, updatedSubmittedProjectData?.updateProject?.project]);

  useEffect(() => {
    if (updatedDraftProjectToOptionScreenData?.updateProjectAsDraft) {
      setProject(updatedDraftProjectToOptionScreenData?.updateProjectAsDraft as ExtendedProject);
    }
  }, [setProject, updatedDraftProjectToOptionScreenData?.updateProjectAsDraft]);

  useEffect(() => {
    if (updatedDraftProjectOnOptionScreenData?.updateProjectAsDraft) {
      setProject(updatedDraftProjectOnOptionScreenData?.updateProjectAsDraft as ExtendedProject);
    }
  }, [setProject, updatedDraftProjectOnOptionScreenData?.updateProjectAsDraft]);

  useEffect(() => {
    if (updatedDraftProjectFromOptionScreenToPreviewScreenData?.updateProjectAsDraft) {
      setProject(updatedDraftProjectFromOptionScreenToPreviewScreenData?.updateProjectAsDraft as ExtendedProject);
    }
  }, [setProject, updatedDraftProjectFromOptionScreenToPreviewScreenData?.updateProjectAsDraft]);

  return {
    createDraftProject,
    updateDraftProject,
    updateDraftProjectAndGoToDifferentTab,
    updateDraftProjectAndGoToProjectsList,
    updateAssetCategoryValueInDraft,
    updateSubmittedProject,
    setCreativeVisibility,
    saveDraftEditOfSubmittedProject,
    submitProject,
  };
};

export default useUpdateProject;
